import * as THREE from 'three';
import { Creature, type CreatureState } from './Creature';
import type { World } from '../world/World';
import type { GameClock } from '../core/Time';
import { mulberry32, seedAt, weightedPick } from '../core/Rng';
import { WATER_LEVEL } from '../world/WorldGen';
import { worldToChunk } from '../world/Chunk';
import { EventBus } from '../core/Events';

/**
 * Canlı yönetimi: doğma, sönme, AI güncellemesi.
 *
 * Performans kuralları (mobil hedefi):
 *  - Oyuncudan uzak canlılar DONDURULUR (AI hesabı yapılmaz, mesh gizlenir).
 *  - Chunk boşalınca içindeki canlılar da kaldırılır.
 *  - AI güncellemesi kademeli: her karede yalnızca bir dilim işlenir.
 */

export interface CreatureSnapshot {
  id: string;
  def: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
  health: number;
  state: CreatureState;
  deathTimer: number;
  stateTimer: number;
  cooldown: number;
}

export interface CreatureHit {
  creature: Creature;
  distance: number;
}

type CreatureEvents = {
  killed: { id: string; def: string; x: number; y: number; z: number; byPlayer: boolean };
  'player:hit': { by: string; damage: number };
  spawned: { id: string; def: string };
};

const MAX_ALIVE = 26;
const ACTIVE_RADIUS = 90;
const DESPAWN_RADIUS = 150;
const SPAWN_INTERVAL = 6;

export class CreatureManager {
  readonly events = new EventBus<CreatureEvents>();
  readonly root = new THREE.Group();
  private list: Creature[] = [];
  private spawnTimer = 0;
  private counter = 0;

  constructor(private world: World, private clock: GameClock) {
    this.root.name = 'creatures';
    world.scene.add(this.root);
  }

  get all(): readonly Creature[] {
    return this.list;
  }

  get aliveCount(): number {
    return this.list.reduce((n, c) => n + (c.alive ? 1 : 0), 0);
  }

  toSnapshot(): CreatureSnapshot[] {
    return this.list.map((c) => ({
      id: c.id,
      def: c.def.id,
      x: c.position.x,
      y: c.position.y,
      z: c.position.z,
      yaw: c.yaw,
      health: c.health,
      state: c.state,
      deathTimer: c.deathTimer,
      stateTimer: c.stateTimer,
      cooldown: c.cooldown,
    }));
  }

  /** Host snapshot'ını kimlik bazında uygular; eksikleri yaratır, fazlaları kaldırır. */
  applySnapshot(states: readonly CreatureSnapshot[]): void {
    const wanted = new Set<string>();
    for (const s of states.slice(0, MAX_ALIVE * 2)) {
      if (!s || typeof s.id !== 'string' || typeof s.def !== 'string' || !s.id || wanted.has(s.id)) continue;
      if (![s.x, s.y, s.z, s.yaw, s.health, s.deathTimer, s.stateTimer, s.cooldown].every(Number.isFinite)) continue;
      let c = this.byId(s.id);
      if (c && c.def.id !== s.def) {
        c.dispose();
        this.list = this.list.filter((x) => x !== c);
        c = null;
      }
      if (!c) {
        c = new Creature(s.id.slice(0, 80), s.def.slice(0, 80), s.x, s.y, s.z, 1);
        this.list.push(c);
        this.root.add(c.mesh);
      }
      wanted.add(c.id);
      c.position.set(s.x, s.y, s.z);
      c.yaw = s.yaw;
      c.health = s.health;
      c.state = isCreatureState(s.state) ? s.state : 'idle';
      c.deathTimer = Math.max(0, s.deathTimer);
      c.stateTimer = s.stateTimer;
      c.cooldown = Math.max(0, s.cooldown);
      c.frozen = false;
      c.mesh.visible = true;
      c.syncMesh(0);
    }
    for (let i = this.list.length - 1; i >= 0; i--) {
      const c = this.list[i]!;
      if (wanted.has(c.id)) continue;
      c.dispose();
      this.list.splice(i, 1);
    }
  }

  /** Guestte AI çalıştırmadan prosedürel mesh animasyonunu ilerletir. */
  updatePresentation(dt: number): void {
    for (const c of this.list) c.syncMesh(dt);
  }

  clear(): void {
    for (const c of this.list) c.dispose();
    this.list = [];
  }

  /** Oyuncu çevresinde canlı doğurur. Gece yırtıcı oranı artar. */
  private trySpawn(playerPos: THREE.Vector3): void {
    if (this.aliveCount >= MAX_ALIVE) return;
    const night = this.clock.isNight;
    const diff = this.world.params.difficulty;

    // Birkaç aday nokta dene: tek denemede su/dik yamaç çıkarsa tur boşa gitmesin
    const { cx, cz } = worldToChunk(playerPos.x, playerPos.z);
    const rand = mulberry32(seedAt(this.world.params.seed, cx, cz, this.counter++ | 0));
    let bx = 0;
    let bz = 0;
    let sample: ReturnType<World['biomeAt']> | null = null;
    for (let attempt = 0; attempt < 8; attempt++) {
      const ang = rand() * Math.PI * 2;
      const r = 28 + rand() * (ACTIVE_RADIUS - 32);
      bx = playerPos.x + Math.cos(ang) * r;
      bz = playerPos.z + Math.sin(ang) * r;
      const s = this.world.biomeAt(bx, bz);
      if (s.underwater || s.height < WATER_LEVEL + 0.5) continue;
      if (this.world.slopeAt(bx, bz) > 1.4) continue;
      sample = s;
      break;
    }
    if (!sample) return;

    const pool = sample.biome.spawns
      .filter((e) => (night ? true : !e.night))
      .map((e) => ({ weight: e.weight * (e.night ? (night ? diff.nightSpawn : 0) : 1), value: e.creature }));
    const defId = weightedPick(rand, pool);
    if (!defId) return;

    const id = `c${this.counter}_${(rand() * 1e6) | 0}`;
    const c = new Creature(id, defId, bx, sample.height, bz, diff.enemyHealth);
    this.list.push(c);
    this.root.add(c.mesh);
    this.events.emit('spawned', { id, def: defId });
  }

  /**
   * Ana güncelleme.
   * @param dt sabit adım
   * @param playerPos oyuncu konumu
   * @param playerNoisy oyuncu koşuyor/vuruyor mu (fark edilme mesafesini artırır)
   */
  update(
    dt: number,
    playerPos: THREE.Vector3,
    playerNoisy: boolean,
    onPlayerHit: (dmg: number, by: string, inflicts?: { effect: string; magnitude: number; duration: number }) => void,
  ): void {
    this.spawnTimer += dt;
    if (this.spawnTimer >= SPAWN_INTERVAL) {
      this.spawnTimer = 0;
      this.trySpawn(playerPos);
    }

    const diff = this.world.params.difficulty;
    const dayNight = this.clock.isNight ? 1.15 : 1;

    for (let i = this.list.length - 1; i >= 0; i--) {
      const c = this.list[i]!;
      const dist = Math.hypot(c.position.x - playerPos.x, c.position.z - playerPos.z);

      // Çok uzak: kaldır
      if (dist > DESPAWN_RADIUS) {
        c.dispose();
        this.list.splice(i, 1);
        continue;
      }

      // Ölüm animasyonu
      if (!c.alive) {
        c.deathTimer -= dt;
        c.syncMesh(dt);
        if (c.deathTimer <= 0) {
          c.dispose();
          this.list.splice(i, 1);
        }
        continue;
      }

      // Uzaktakiler donar: AI hesabı yapılmaz
      c.frozen = dist > ACTIVE_RADIUS * 0.75;
      c.mesh.visible = dist < ACTIVE_RADIUS;
      if (c.frozen) continue;

      this.updateAi(c, dt, dist, playerPos, playerNoisy, dayNight, diff.enemyDamage, onPlayerHit);
      c.syncMesh(dt);
    }
  }

  private updateAi(
    c: Creature,
    dt: number,
    dist: number,
    playerPos: THREE.Vector3,
    playerNoisy: boolean,
    dayNight: number,
    damageMul: number,
    onPlayerHit: (dmg: number, by: string, inflicts?: { effect: string; magnitude: number; duration: number }) => void,
  ): void {
    const def = c.def;
    const sense = def.senseRange * this.world.params.difficulty.aggroRange * (playerNoisy ? 1.35 : 1) * dayNight;
    c.cooldown = Math.max(0, c.cooldown - dt);
    c.stateTimer -= dt;

    // Durum seçimi
    if (def.behavior === 'aggressive') {
      if (dist < sense) c.state = dist <= def.attackRange ? 'attack' : 'chase';
      else if (c.state === 'chase' || c.state === 'attack') c.state = 'idle';
    } else if (def.behavior === 'skittish') {
      if (dist < sense * 0.7) c.state = 'flee';
      else if (c.state === 'flee') c.state = 'idle';
    } else if (def.behavior === 'defensive') {
      // Yalnızca vurulunca saldırır (health < max ise chase)
      if (c.health < def.health && dist < sense) c.state = dist <= def.attackRange ? 'attack' : 'chase';
      else if (c.state === 'chase' || c.state === 'attack') c.state = 'idle';
    }

    if (c.state === 'idle' || c.state === 'wander') {
      if (c.stateTimer <= 0) {
        c.stateTimer = 2 + Math.random() * 4;
        c.state = Math.random() < 0.55 ? 'idle' : 'wander';
        if (c.state === 'wander') c.yaw += (Math.random() - 0.5) * 2.4;
      }
    }

    // Hareket
    let speed = 0;
    if (c.state === 'wander') speed = def.moveSpeed;
    else if (c.state === 'chase') speed = def.runSpeed;
    else if (c.state === 'flee') speed = def.runSpeed;

    if (c.state === 'chase' || c.state === 'attack') {
      c.yaw = Math.atan2(playerPos.x - c.position.x, playerPos.z - c.position.z);
    } else if (c.state === 'flee') {
      c.yaw = Math.atan2(c.position.x - playerPos.x, c.position.z - playerPos.z);
    }

    if (speed > 0) {
      const nx = c.position.x + Math.sin(c.yaw) * speed * dt;
      const nz = c.position.z + Math.cos(c.yaw) * speed * dt;
      // Suya ve dik yamaca girmesin, yapılara çarpmasın
      const h = this.world.heightAt(nx, nz);
      if (h > WATER_LEVEL + 0.3 && !this.world.blockedAt(nx, nz, h, def.size * 0.4)) {
        c.position.x = nx;
        c.position.z = nz;
        c.position.y = h;
      } else {
        c.yaw += Math.PI * 0.6;
      }
    } else {
      c.position.y = this.world.heightAt(c.position.x, c.position.z);
    }

    // Saldırı
    if (c.state === 'attack' && c.cooldown <= 0 && def.damage > 0) {
      c.cooldown = def.attackCooldown;
      c.beginAttack();
      onPlayerHit(def.damage * damageMul, def.id, def.inflicts);
      this.events.emit('player:hit', { by: def.id, damage: def.damage * damageMul });
    }
  }

  /** Oyuncunun bakış konisindeki en yakın canlıyı bulur. */
  pick(origin: THREE.Vector3, dir: THREE.Vector3, range: number): CreatureHit | null {
    let best: CreatureHit | null = null;
    for (const c of this.list) {
      if (!c.alive) continue;
      const to = new THREE.Vector3(c.position.x - origin.x, c.position.y + c.def.size * 0.5 - origin.y, c.position.z - origin.z);
      const dist = to.length();
      if (dist > range + c.def.size) continue;
      to.normalize();
      // Yakın mesafede daha geniş koni: mobilde nişan almak kolay olsun
      const minDot = dist < 3 ? 0.35 : 0.72;
      if (to.dot(dir) < minDot) continue;
      if (!best || dist < best.distance) best = { creature: c, distance: dist };
    }
    return best;
  }

  byId(id: string): Creature | null {
    return this.list.find((c) => c.id === id) ?? null;
  }

  /** Hasar uygula; ölürse loot olayı yayınlanır. */
  damage(id: string, amount: number, byPlayer: boolean): { killed: boolean; creature: Creature | null } {
    const c = this.byId(id);
    if (!c || !c.alive) return { killed: false, creature: null };
    c.health -= amount;
    c.flashHit();
    // Vurulan pasif canlı kaçar, savunmacı saldırır
    if (c.def.behavior === 'skittish' || c.def.behavior === 'passive' || c.def.behavior === 'mount') c.state = 'flee';
    else c.state = 'chase';
    c.stateTimer = 4;
    if (c.health <= 0) {
      c.state = 'dead';
      c.deathTimer = 1.2;
      this.events.emit('killed', { id: c.id, def: c.def.id, x: c.position.x, y: c.position.y, z: c.position.z, byPlayer });
      return { killed: true, creature: c };
    }
    return { killed: false, creature: c };
  }

  dispose(): void {
    this.clear();
    this.root.removeFromParent();
  }
}

function isCreatureState(value: unknown): value is CreatureState {
  return value === 'idle' || value === 'wander' || value === 'flee' || value === 'chase' || value === 'attack' || value === 'dead';
}
